// Which TradePrep website this is: tradeprep.co.nz (New Zealand) or
// tradeprep.com.au (Australia). Both are the same app on the same accounts and
// database; the site decides the default currency, how money and dates read,
// the greeting and the support address. Anything else (localhost, a preview
// address) is the New Zealand site unless VITE_SITE says otherwise.

import { DEFAULT_SITE, parseSiteCode, type Site, type SiteCode, SITES, siteFromHost } from '@core/site.ts';
import { env } from './env';
import { isNative } from './platform/platform';

export { SITES, type Site, type SiteCode };

let forced: SiteCode | null = null;

/** For tests: pretend to be a site. `null` goes back to the real address. */
export function setSiteForTests(code: SiteCode | null) {
  forced = code;
}

export function getSite(): Site {
  if (forced) return SITES[forced];
  const fromHost = typeof window !== 'undefined' ? siteFromHost(window.location.hostname) : null;
  if (fromHost) return SITES[fromHost];
  const configured = import.meta.env.VITE_SITE as string | undefined;
  return SITES[configured ? parseSiteCode(configured) : DEFAULT_SITE];
}

/** The address people are on: where links, QR codes and search listings point. The native app has no web address of its own. */
export function siteOrigin(): string {
  return isNative() ? env.appUrl.replace(/\/$/, '') : window.location.origin;
}

/**
 * The website's own address (https://tradeprep.co.nz or https://tradeprep.com.au), for people to type or paste
 * into another browser. Unlike siteOrigin() it never reflects the address the page happens to be open on,
 * which can be an odd one inside an email app's built-in browser.
 */
export const siteAddress = (): string => `https://${getSite().domain}`;

/** The currency to start new products and balances in: this site's, if prices can be set in it. */
export function defaultCurrencyFor(supported: readonly string[] | null | undefined, fallback: string): string {
  const own = getSite().currency;
  return !supported || supported.includes(own) ? own : fallback;
}

export const supportEmail = () => getSite().supportEmail;
